import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PlusIcon } from '../../../assets/icons';
import {
  ProgramAttachment,
  SOCIAL_MEDIA_CONTENT_TYPE_REEL,
  SocialMediaAttachment,
  SocialMediaContentType,
} from '../../types';
import { AddAttachment } from '../attachments/AddAttachment';
import { FieldArray, FieldArrayRenderProps } from 'formik';
import { MediaPreview } from './MediaPreview';
import { deleteAttachment, isVideo } from '../attachments/util';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import clsx from 'clsx';
import { AttachmentsModal } from '../attachments/AttachmentsModal';
import { BoxSpinner } from '../../../commons/components/Spinner';

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png'];
const VIDE_EXTENSIONS = ['.mov', '.mp4'];
const VIDEO_AND_IMAGE_EXTENSIONS = [...IMAGE_EXTENSIONS, ...VIDE_EXTENSIONS];

export const Carousel = (props: {
  programId: string;
  type: SocialMediaContentType;
  socialMediaAttachments: SocialMediaAttachment[];
  attachments: ProgramAttachment[] | null | undefined;
  onAttachmentIdsChange: (newAttachments: SocialMediaAttachment[]) => void;
}) => {
  const { programId, type, socialMediaAttachments, attachments, onAttachmentIdsChange } = props;

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const carouselAttachments = useMemo(
    () =>
      attachments
        ? (socialMediaAttachments
            .map((socialMediaAttachment): ProgramAttachment | undefined =>
              attachments.find(
                (attachment) =>
                  attachment.status !== 'DELETED' && attachment.id === socialMediaAttachment.id
              )
            )
            .filter((x) => !!x) as ProgramAttachment[])
        : null,
    [attachments, socialMediaAttachments]
  );

  const handleItemClick = useCallback(
    (attachment: ProgramAttachment) => {
      if (!carouselAttachments) return;
      const index = carouselAttachments.findIndex((a) => a.id === attachment.id);
      if (index >= 0) {
        setSelectedIndex(index);
      }
    },
    [carouselAttachments]
  );

  const closeAttachmentView = useCallback(() => {
    setSelectedIndex(null);
  }, []);

  const handleDeleteAttachment = (
    attachment: ProgramAttachment,
    arrayHelpers: FieldArrayRenderProps
  ) => {
    if (!attachments) return;
    const newAttachmentIds = socialMediaAttachments.filter(
      (socialMediaAttachment) => socialMediaAttachment.id !== attachment.id
    );
    onAttachmentIdsChange(newAttachmentIds);
    deleteAttachment(attachments, arrayHelpers, attachment);
  };

  useEffect(() => {
    if (carouselAttachments?.length === 0) {
      setSelectedIndex(null);
    }
  }, [carouselAttachments?.length]);

  return (
    <div className="carousel__container">
      <FieldArray name="attachments">
        {(arrayHelpers) => {
          return (
            <>
              {!carouselAttachments ? (
                <BoxSpinner />
              ) : (
                carouselAttachments.length > 0 && (
                  <DragDropContext
                    onDragEnd={(result: DropResult) => {
                      if (!result.destination) return;
                      if (result.source.droppableId !== result.destination.droppableId) return;
                      const fromIndex = result.source.index;
                      const toIndex = result.destination.index;
                      if (fromIndex === toIndex) return;
                      if (
                        fromIndex < 0 ||
                        fromIndex >= socialMediaAttachments.length ||
                        toIndex < 0 ||
                        toIndex >= socialMediaAttachments.length
                      )
                        return;
                      const newOrder = [...socialMediaAttachments];
                      const [moved] = newOrder.splice(fromIndex, 1);
                      newOrder.splice(toIndex, 0, moved);
                      onAttachmentIdsChange(newOrder);
                    }}
                  >
                    <Droppable droppableId="carousel-droppable" direction="horizontal">
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={clsx('carousel__images-container', {
                            'carousel__images-container--dragging': snapshot.isDraggingOver,
                          })}
                        >
                          {carouselAttachments.map((attachment, index) => (
                            <Draggable
                              draggableId={attachment.id}
                              index={index}
                              key={attachment.id}
                            >
                              {(draggableProvided, x) => (
                                <div
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                  role="button"
                                  tabIndex={0}
                                  className={x.draggingOver ? 'carousel__image--dragging' : ''}
                                  onClick={() => handleItemClick(attachment)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleItemClick(attachment);
                                    }
                                  }}
                                >
                                  <MediaPreview
                                    attachment={attachment}
                                    programId={programId}
                                    canDelete={!!attachments}
                                    onDeleteAttachment={(attachment) =>
                                      handleDeleteAttachment(attachment, arrayHelpers)
                                    }
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )
              )}

              <AddAttachment
                maxAttachments={type === SOCIAL_MEDIA_CONTENT_TYPE_REEL ? 1 : 10}
                maxSizeMb={500}
                notDeletedAttachmentsCount={socialMediaAttachments.length}
                arrayHelpers={arrayHelpers}
                icon={<PlusIcon />}
                buttonClassName="carousel__add-button"
                accept={
                  type === SOCIAL_MEDIA_CONTENT_TYPE_REEL
                    ? VIDE_EXTENSIONS
                    : VIDEO_AND_IMAGE_EXTENSIONS
                }
                hideAddIfMaxAttachments={type === SOCIAL_MEDIA_CONTENT_TYPE_REEL}
                onAdd={(newAttachments) => {
                  const newAttachmentIds: SocialMediaAttachment[] = [
                    ...socialMediaAttachments,
                    ...newAttachments.map(
                      (a): SocialMediaAttachment => ({
                        id: a.id,
                        type: isVideo(a.file!!.type) ? 'VIDEO' : 'IMAGE',
                      })
                    ),
                  ];
                  onAttachmentIdsChange(newAttachmentIds);
                }}
              />
              {carouselAttachments && (
                <AttachmentsModal
                  programId={programId}
                  nonDeletedAttachments={carouselAttachments}
                  selectedIndex={selectedIndex}
                  onClose={closeAttachmentView}
                  onDelete={(attachment) => handleDeleteAttachment(attachment, arrayHelpers)}
                />
              )}
            </>
          );
        }}
      </FieldArray>
    </div>
  );
};
